import {queryOptions} from '@tanstack/react-query';

import config from '../../config';
import {AirtablePromotionItem} from '../../models/airtable';
import apiService from '../../transport/apiService';
import {logger, serializeError} from '../../utils/logger';

import {promotionsKeys} from './keys';
import {PromotionArms} from './types';

/** Mirrors `queryClient.ts` defaults — promotions arms retry inside queryFn. */
const QUERY_RETRY_COUNT = 2;
const queryRetryDelay = (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000);

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const isNonArrayBodyError = (error: unknown): boolean =>
  error instanceof Error && error.message.includes('returned non-array body');

const fetchArmWithRetry = async (
  fetchFn: () => Promise<AirtablePromotionItem[]>,
  arm: 'live' | 'winners',
): Promise<AirtablePromotionItem[]> => {
  let lastError: unknown;
  for (let attempt = 0; attempt <= QUERY_RETRY_COUNT; attempt++) {
    try {
      const value = await fetchFn();
      if (!Array.isArray(value)) {
        throw new Error(`promotions ${arm} arm returned non-array body`);
      }
      return value;
    } catch (error) {
      if (isNonArrayBodyError(error)) {
        throw error;
      }
      lastError = error;
      if (attempt < QUERY_RETRY_COUNT) {
        await sleep(queryRetryDelay(attempt));
      }
    }
  }
  throw lastError;
};

/**
 * DIS-1567: two independent reads, not one. Each arm retries internally with the
 * same count/backoff as `queryClient` defaults so a single 500 does not bypass
 * retry or dehydrate as `{live: [], winners}`.
 *
 * - **Live** still failing after retries → throw. Error queries are not dehydrated.
 * - **Winners** still failing after retries → `[]` + error log (DIS-1563).
 *
 * Returns RAW arms. Derivation is deliberately not done here: this result is
 * persisted for 24h (persistConfig.ts:13) and the derivation is auth-dependent.
 */
export const fetchPromotionArms = async (
  storeNumber: string,
  storeState?: string,
): Promise<PromotionArms> => {
  const fetchLiveArm = async (): Promise<AirtablePromotionItem[]> => {
    try {
      return await fetchArmWithRetry(
        () => apiService.getLivePromotions(storeNumber, storeState),
        'live',
      );
    } catch (error) {
      logger.error('promotions', 'Error loading live promotions', serializeError(error));
      throw error;
    }
  };

  const fetchWinnersArm = async (): Promise<AirtablePromotionItem[]> => {
    try {
      return await fetchArmWithRetry(
        () => apiService.getDrawnWinnersPromotions(storeNumber, storeState),
        'winners',
      );
    } catch (error) {
      logger.error('promotions', 'Error loading drawn winners promotions', serializeError(error));
      return [];
    }
  };

  const [live, winners] = await Promise.all([fetchLiveArm(), fetchWinnersArm()]);
  return {live, winners};
};

/**
 * The ONE place that decides whether a store has promotions to fetch. Every
 * guard in the promotions layer derives from this so they cannot drift:
 * `promotionsQueryOptions.enabled`, `usePromotions`'s terminal
 * `hasFetchedPromotions`, and `ensurePromotions`'s early return.
 *
 * - `!storeNumber` — three call sites read the store number off an optional
 *   `selectedStore`. An undefined store number reaches the middleware and 400s.
 * - `config.GIFT_CARD_STORE` — the gift card store has no promotions, which is
 *   why `rootStore.initialiseSelectedStoreLoyaltyDependentData` refuses to
 *   prefetch for it. DIS-1567 Task 4 turned thirteen passive store reads into
 *   thirteen mount-time fetch triggers, and `GiftCardsOptionsScreen` is
 *   registered in the gift-card navigator (`allStoresAppRoutes.tsx`) and
 *   branches on `isSelectedStoreGiftCardStore`, so it demonstrably renders with
 *   that store selected. Without this, opening it fires both arms into a
 *   guaranteed 400, multiplied by internal arm retries inside `fetchPromotionArms`.
 */
export const promotionsEnabled = (storeNumber?: string) =>
  !!storeNumber && storeNumber !== config.GIFT_CARD_STORE;

/**
 * Single source of query config, shared by `usePromotions` and the imperative
 * helpers in promotionsAccess.ts.
 */
export const promotionsQueryOptions = (storeNumber?: string, storeState?: string) => {
  // Uppercased HERE rather than at each call site. The middleware builds the
  // Airtable filter as `FIND('${storeState}', {State})`
  // (lmg-digital-stores apps/middleware/src/utils/airtable/filters.ts) and
  // Airtable's FIND() is case-sensitive, so a lower-case state matches nothing
  // and returns an empty promotions list with no error and no empty state — a
  // blank Current Promotions screen. Six of the seven FE call sites that send
  // `administrative_area` already normalise it; promotions was the outlier, and
  // `PromotionsListScreenBase.refresh` (the one path that used to uppercase) is
  // now a plain `refetch()`. Normalising in the one place that owns both the
  // cache key and the request keeps every caller on a single cache entry AND on
  // safe casing. `config.DEFAULT_STORE_STATE` is already uppercase in every
  // `.env.*.yml`, so the default-store and selected-store paths finally agree.
  const normalisedStoreState = storeState?.toUpperCase();
  return queryOptions({
    queryKey: promotionsKeys.list(storeNumber, normalisedStoreState),
    queryFn: () => fetchPromotionArms(storeNumber as string, normalisedStoreState),
    enabled: promotionsEnabled(storeNumber),
    // Retries run inside `fetchPromotionArms`; query-level retry would stack
    // three internal attempts on every outer attempt from `queryClient` defaults.
    retry: false,
  });
};
